/**
 * A porta entre a tela das integrações (módulo 04; 0104) e o servidor. A mesa e
 * o celular usam as mesmas chamadas e os mesmos ganchos de leitura.
 */
import { useQuery } from '@tanstack/react-query';

import { BASE_DA_API, CREDENCIAIS_DA_API } from '@/lib/api';
import { ApiIndisponivel, comCorpo, pedir, RecusaDaApi } from '@/lib/pedido';

import type {
  BaixaDaIntegracao,
  CadastroDeEntrada,
  Contingencia,
  Correspondencia,
  DetalheDoLote,
  Divergencia,
  Entidade,
  EntidadeDeImportacao,
  FiltroDaLinha,
  Formato,
  Lote,
  NotaDaIntegracao,
  RespostaDaBaixa,
  RespostaDaImportacao,
  RespostaDaMatriz,
  RespostaDaPromocao,
  RespostaDaSaida,
  RespostaDaSaidaDeRegistro,
  RespostaDosTitulos,
  RespostaDoRetorno,
  RespostaDoTitulo,
  Resumo,
  SistemaExterno,
  TipoDeDivergencia,
  TituloDaIntegracao,
} from './integracoes';

/** A chave de idempotência da requisição: um uuid novo por clique. */
export const novaChave = (): string => crypto.randomUUID();

const comChave = <T,>(caminho: string, corpo: unknown, chave: string): Promise<T> =>
  pedir<T>(caminho, {
    method: 'POST',
    body: JSON.stringify(corpo),
    headers: { 'content-type': 'application/json', 'idempotency-key': chave },
  });

export const apiDasIntegracoes = {
  resumo: () => pedir<Resumo>('/integracoes/resumo'),
  matriz: () => pedir<RespostaDaMatriz>('/integracoes/matriz'),
  sistemas: () => pedir<{ sistemas: readonly SistemaExterno[] }>('/integracoes/sistemas'),
  lotes: (direcao: 'entrada' | 'saida' | undefined, pagina: number) =>
    pedir<{ lotes: readonly Lote[]; total: number; pagina: number; porPagina: number }>(
      `/integracoes/lotes?pagina=${String(pagina)}${direcao ? `&direcao=${direcao}` : ''}`,
    ),
  lote: (id: string, filtro: FiltroDaLinha, pagina: number) =>
    pedir<DetalheDoLote>(`/integracoes/lotes/${encodeURIComponent(id)}?filtro=${filtro}&pagina=${String(pagina)}`),
  correspondencias: (busca: string, entidade: CadastroDeEntrada | undefined, pagina: number) =>
    pedir<{ correspondencias: readonly Correspondencia[]; total: number; pagina: number; porPagina: number }>(
      `/integracoes/correspondencias?pagina=${String(pagina)}${entidade ? `&entidade=${entidade}` : ''}${busca.trim() ? `&busca=${encodeURIComponent(busca.trim())}` : ''}`,
    ),
  registros: (entidade: CadastroDeEntrada, busca: string) =>
    pedir<{ registros: readonly { id: string; codigo: string; nome: string; ativo: boolean }[] }>(
      `/integracoes/registros?entidade=${entidade}${busca.trim() ? `&busca=${encodeURIComponent(busca.trim())}` : ''}`,
    ),
  divergencias: (situacao: 'aberta' | 'resolvida', pagina: number) =>
    pedir<{ divergencias: readonly Divergencia[]; total: number; pagina: number; porPagina: number }>(
      `/integracoes/divergencias?situacao=${situacao}&pagina=${String(pagina)}`,
    ),
  configuracao: () => pedir<{ toleranciaReais: number }>('/integracoes/configuracao'),

  cadastrarSistema: (d: { codigo: string; nome: string; tipo: string; observacao?: string }) =>
    comCorpo<{ id: string; codigo: string; nome: string; tipo: string }>('POST', '/integracoes/sistemas', d),
  proporLinha: (d: {
    entidade: Entidade; sistemaMestreId: string | null; sistemaParceiroId: string | null; formato: Formato;
    frequenciaDias: number; contingencia: string; inicio: string;
  }) => comCorpo<{ id: string; entidade: Entidade; direcao: 'entrada' | 'saida' | 'nenhuma'; inicio: string }>('POST', '/integracoes/matriz', d),
  decidirLinha: (id: string, decisao: 'aprovada' | 'recusada', nota: string) =>
    comCorpo<{ id: string; entidade: Entidade; decisao: 'aprovada' | 'recusada'; inicio: string }>('POST', `/integracoes/matriz/${encodeURIComponent(id)}/aprovacao`, { decisao, nota }),
  encerrarLinha: (id: string, ultimoDia: string, motivo: string) =>
    comCorpo<{ id: string; entidade: Entidade; ultimoDia: string }>('POST', `/integracoes/matriz/${encodeURIComponent(id)}/encerramento`, { ultimoDia, motivo }),
  importar: (d: { entidade: EntidadeDeImportacao; nomeArquivo: string; conteudo: string }, chave: string) =>
    comChave<RespostaDaImportacao>('/integracoes/lotes', d, chave),
  promover: (id: string) => comCorpo<RespostaDaPromocao>('POST', `/integracoes/lotes/${encodeURIComponent(id)}/promocao`, {}),
  descartarEntrada: (id: string, motivo: string) =>
    comCorpo<{ loteId: string; nomeArquivo: string }>('POST', `/integracoes/lotes/${encodeURIComponent(id)}/descarte`, { motivo }),
  descartarSaida: (id: string, motivo: string) =>
    comCorpo<{ loteId: string; nomeArquivo: string }>('POST', `/integracoes/saidas/${encodeURIComponent(id)}/descarte`, { motivo }),
  gerarSaida: (competencia: string, motivo: string | undefined, chave: string) =>
    comChave<RespostaDaSaida>('/integracoes/saidas', motivo ? { competencia, motivo } : { competencia }, chave),
  gerarSaidaDeRegistro: (entidade: Exclude<RespostaDaSaidaDeRegistro['entidade'], 'lancamento_gerencial'>, chave: string) =>
    comChave<RespostaDaSaidaDeRegistro>('/integracoes/saidas', { entidade }, chave),
  receberRetorno: (id: string, nomeArquivo: string, conteudo: string) =>
    comCorpo<RespostaDoRetorno>('POST', `/integracoes/saidas/${encodeURIComponent(id)}/retorno`, { nomeArquivo, conteudo }),
  vincular: (d: { sistemaId: string; entidade: CadastroDeEntrada; chaveExterna: string; registroId: string }) =>
    comCorpo<{ id: string; chaveExterna: string; entidade: CadastroDeEntrada; registro: { codigo: string; nome: string } | null }>('POST', '/integracoes/correspondencias', d),
  trocarCorrespondencia: (id: string, registroId: string, motivo: string) =>
    comCorpo<{ id: string; chaveExterna: string; antes: string | null; depois: { codigo: string; nome: string } | null }>(
      'POST', `/integracoes/correspondencias/${encodeURIComponent(id)}/troca`, { registroId, motivo },
    ),
  assumir: (id: string) => comCorpo<{ id: string; tipo: TipoDeDivergencia }>('POST', `/integracoes/divergencias/${encodeURIComponent(id)}/responsavel`, {}),
  resolver: (id: string, resolucao: string, justificativa: string) =>
    comCorpo<{ id: string; tipo: TipoDeDivergencia; diferenca: number; resolucao: string }>(
      'POST', `/integracoes/divergencias/${encodeURIComponent(id)}/resolucao`, { resolucao, justificativa },
    ),
  /* 0108: os títulos, as baixas, as notas e as contingências. */
  titulos: (tipo: 'a_pagar' | 'a_receber' | undefined, situacao: 'em_aberto' | 'todos', pagina: number) =>
    pedir<RespostaDosTitulos>(`/integracoes/titulos?situacao=${situacao}&pagina=${String(pagina)}${tipo ? `&tipo=${tipo}` : ''}`),
  titulo: (id: string) => pedir<{ titulo: TituloDaIntegracao; baixas: readonly BaixaDaIntegracao[] }>(`/integracoes/titulos/${encodeURIComponent(id)}`),
  notas: (tipo: 'entrada' | 'saida' | undefined, pagina: number) =>
    pedir<{ notas: readonly NotaDaIntegracao[]; total: number; pagina: number; porPagina: number }>(`/integracoes/notas?pagina=${String(pagina)}${tipo ? `&tipo=${tipo}` : ''}`),
  contingencias: (situacao: 'pendente' | 'casada') => pedir<{ contingencias: readonly Contingencia[]; pendentes: number }>(`/integracoes/contingencias?situacao=${situacao}`),
  ligacoes: (tipo: 'a_pagar' | 'a_receber', busca: string) =>
    pedir<{ ligacoes: readonly { id: string; numero: string; detalhe: string }[] }>(`/integracoes/ligacoes?tipo=${tipo}${busca.trim() ? `&busca=${encodeURIComponent(busca.trim())}` : ''}`),
  lancarTitulo: (d: {
    tipo: 'a_pagar' | 'a_receber'; numero: string; contraparteId: string; emitidoEm: string; vencimento: string; valor: number;
    ligacaoId?: string | null; competencia?: string; contingencia?: string;
  }) => comCorpo<RespostaDoTitulo>('POST', '/integracoes/titulos', d),
  baixar: (id: string, d: { pagoEm: string; valor: number; contingencia?: string }) => comCorpo<RespostaDaBaixa>('POST', `/integracoes/titulos/${encodeURIComponent(id)}/baixas`, d),
  cancelarTitulo: (id: string, d: { motivo: string; contingencia?: string }) =>
    comCorpo<{ id: string; numero: string; motivo: string }>('POST', `/integracoes/titulos/${encodeURIComponent(id)}/cancelamento`, d),
  registrarNota: (d: {
    tipo: 'entrada' | 'saida'; numero: string; serie?: string; chaveAcesso: string; emitidaEm: string; valor: number;
    contraparteId: string; ligacaoId?: string | null; contingencia: string;
  }) => comCorpo<{ id: string; tipo: 'entrada' | 'saida'; numero: string; chaveAcesso: string; valor: number }>('POST', '/integracoes/notas', d),
  mudarTolerancia: (toleranciaReais: number, motivo: string) =>
    pedir<{ toleranciaReais: number; antes: number }>('/integracoes/configuracao', {
      method: 'PUT',
      body: JSON.stringify({ toleranciaReais, motivo }),
    }),
};

/**
 * BAIXAR O ARQUIVO DA SAIDA: a primeira rota que devolve arquivo gerado. O
 * servidor refaz o arquivo das linhas guardadas e confere o hash; a tela só
 * salva o que veio, com o nome que o servidor deu.
 */
export async function baixarArquivoDaSaida(id: string): Promise<string> {
  let r: Response;
  try {
    r = await fetch(`${BASE_DA_API}/integracoes/saidas/${encodeURIComponent(id)}/arquivo`, { credentials: CREDENCIAIS_DA_API });
  } catch {
    throw new ApiIndisponivel();
  }
  if (!r.ok) {
    const corpo = (await r.json().catch(() => ({}))) as { erro?: string; comoResolver?: string };
    throw new RecusaDaApi(corpo.erro ?? 'o arquivo não foi baixado', corpo.comoResolver ?? 'Tente de novo; se continuar, avise o suporte.', r.status);
  }
  const nome = /filename="([^"]+)"/.exec(r.headers.get('content-disposition') ?? '')?.[1] ?? `saida-${id}.csv`;
  const url = URL.createObjectURL(await r.blob());
  const a = document.createElement('a');
  a.href = url;
  a.download = nome;
  a.click();
  URL.revokeObjectURL(url);
  return nome;
}

/** O arquivo lido como texto: UTF-8, e windows-1252 quando o UTF-8 não serve (o Excel em português). */
export async function lerArquivo(arquivo: File): Promise<string> {
  const bytes = await arquivo.arrayBuffer();
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  } catch {
    return new TextDecoder('windows-1252').decode(bytes);
  }
}

/** As leituras, com a chave do cache que as gravações invalidam (tudo sob `integracoes`). */
export const CHAVE_DAS_INTEGRACOES = ['integracoes'] as const;
export const useResumoDasIntegracoes = () => useQuery({ queryKey: ['integracoes', 'resumo'], queryFn: apiDasIntegracoes.resumo });
export const useMatriz = () => useQuery({ queryKey: ['integracoes', 'matriz'], queryFn: apiDasIntegracoes.matriz });
export const useSistemas = () => useQuery({ queryKey: ['integracoes', 'sistemas'], queryFn: apiDasIntegracoes.sistemas });
export const useLotes = (direcao: 'entrada' | 'saida' | undefined, pagina: number) =>
  useQuery({ queryKey: ['integracoes', 'lotes', direcao, pagina], queryFn: () => apiDasIntegracoes.lotes(direcao, pagina) });
export const useLote = (id: string, filtro: FiltroDaLinha, pagina: number) =>
  useQuery({ queryKey: ['integracoes', 'lote', id, filtro, pagina], queryFn: () => apiDasIntegracoes.lote(id, filtro, pagina) });
export const useCorrespondencias = (busca: string, entidade: CadastroDeEntrada | undefined, pagina: number) =>
  useQuery({ queryKey: ['integracoes', 'correspondencias', busca, entidade, pagina], queryFn: () => apiDasIntegracoes.correspondencias(busca, entidade, pagina) });
export const useRegistros = (entidade: CadastroDeEntrada, busca: string, ligado: boolean) =>
  useQuery({ queryKey: ['integracoes', 'registros', entidade, busca], queryFn: () => apiDasIntegracoes.registros(entidade, busca), enabled: ligado });
export const useDivergencias = (situacao: 'aberta' | 'resolvida', pagina: number) =>
  useQuery({ queryKey: ['integracoes', 'divergencias', situacao, pagina], queryFn: () => apiDasIntegracoes.divergencias(situacao, pagina) });
export const useConfiguracaoDaTroca = () => useQuery({ queryKey: ['integracoes', 'configuracao'], queryFn: apiDasIntegracoes.configuracao });
export const useTitulosDaIntegracao = (tipo: 'a_pagar' | 'a_receber' | undefined, situacao: 'em_aberto' | 'todos', pagina: number) =>
  useQuery({ queryKey: ['integracoes', 'titulos', tipo, situacao, pagina], queryFn: () => apiDasIntegracoes.titulos(tipo, situacao, pagina) });
export const useTituloDaIntegracao = (id: string | null) =>
  useQuery({ queryKey: ['integracoes', 'titulo', id], queryFn: () => apiDasIntegracoes.titulo(id ?? ''), enabled: id !== null });
export const useNotasDaIntegracao = (tipo: 'entrada' | 'saida' | undefined, pagina: number) =>
  useQuery({ queryKey: ['integracoes', 'notas', tipo, pagina], queryFn: () => apiDasIntegracoes.notas(tipo, pagina) });
export const useContingencias = (situacao: 'pendente' | 'casada') =>
  useQuery({ queryKey: ['integracoes', 'contingencias', situacao], queryFn: () => apiDasIntegracoes.contingencias(situacao) });
export const useLigacoes = (tipo: 'a_pagar' | 'a_receber', busca: string, ligado: boolean) =>
  useQuery({ queryKey: ['integracoes', 'ligacoes', tipo, busca], queryFn: () => apiDasIntegracoes.ligacoes(tipo, busca), enabled: ligado });
